<script lang="ts">
import { required } from '@vee-validate/rules';
import { Field } from 'vee-validate';
import { computed, defineComponent, PropType, ref } from 'vue';

import { AvsIncomplete, AvsResponse } from '@/api/address/verification';
import FieldErrorIcon from '@/components/base/assets/FieldErrorIcon.vue';
import InlineError from '@/components/base/form/InlineError.vue';
import Input from '@/components/base/form/Input.vue';
import Modal from '@/components/base/layout/Modal.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import MailingAddress from '@/components/checkout/steps/address/MailingAddress.vue';
import { FlexibleMessageValidator, useForm } from '@/composables/useForm';
import { NutsAddress } from '@/utils/address';

export default defineComponent({
  name: 'AvsModal',
  props: {
    addressProvided: { required: false, type: Object as PropType<NutsAddress> },
    avsStatus: { required: false, type: Object as PropType<AvsResponse> },
    displayAddressVerificationModal: { required: true, type: Boolean },
  },
  components: {
    // eslint-disable-next-line vue/no-reserved-component-names
    Input,
    Field,
    FieldErrorIcon,
    InlineError,
    MailingAddress,
    Modal,
    ThemedButton,
  },
  emits: [
    'close-avs-modal',
    'confirm-house-number',
    'confirm-unit-number',
    'use-entered-address',
    'use-suggested-address',
  ],
  setup(props, { emit }) {
    const { errorMessages, validatorFailed } = useForm();

    const houseInfo = ref<string>();
    const unitInfo = ref<string>();
    const postalCodeChange = computed<boolean>(() => {
      if (props.avsStatus?.status === 'requireUnitNumber') {
        const { suggestions } = props.avsStatus;
        return (
          !!suggestions?.length && props.addressProvided?.postalCode !== suggestions[0].postalCode
        );
      }

      return false;
    });

    const confirmUnitNumber = () => {
      const { suggestions } = props.avsStatus as AvsIncomplete;
      if (postalCodeChange.value && suggestions?.length) {
        emit('confirm-unit-number', unitInfo.value, suggestions[0].postalCode);
      } else {
        emit('confirm-unit-number', unitInfo.value);
      }
    };

    const requireBuildingInfo: FlexibleMessageValidator<string> = (value) =>
      required(value) || errorMessages.genericRequire.required;
    const requireUnitInfo: FlexibleMessageValidator<string> = (value) =>
      required(value) || errorMessages.genericRequire.required;

    return {
      closeAvsModal: () => emit('close-avs-modal'),
      confirmHouseNumber: () => emit('confirm-house-number', houseInfo.value),
      confirmUnitNumber,
      postalCodeChange,
      houseInfo,
      requireBuildingInfo,
      requireUnitInfo,
      unitInfo,
      useEnteredAddress: () => emit('use-entered-address'),
      useSuggestedAddress: () => emit('use-suggested-address'),
      validatorFailed,
    };
  },
});
</script>

<template>
  <Modal
    aria-label="Verify address"
    data-test="address-verification-modal"
    :hasControls="false"
    :isOpen="displayAddressVerificationModal"
    width="md:max-w-2xl"
    @handle-close="closeAvsModal"
  >
    <template v-slot:header>
      <div>
        <strong class="mt-2 text-xl font-bold leading-5 md:text-2xl sm:mt-0">
          Verify Your Shipping Address
        </strong>
      </div>
    </template>
    <template v-slot:body>
      <div v-if="avsStatus" class="mt-5">
        <div v-if="avsStatus.status === 'interactionRequired'">
          <div class="text-sm leading-5 md:text-base">
            To ensure accurate delivery, consider the changes highlighted below.
          </div>
          <div class="block gap-20 mt-12 sm:flex">
            <div>
              <div class="font-bold">Suggested Address</div>
              <MailingAddress :address="avsStatus.address" class="mt-2 font-normal" />
              <ThemedButton class="mt-7 sm:mt-12" theme="gray" @click="useSuggestedAddress">
                Use Suggested Address
              </ThemedButton>
            </div>
            <div class="mt-10 sm:mt-0">
              <div class="flex">
                <div class="font-bold">You Entered</div>
                <div
                  class="self-center pl-4 text-xs underline cursor-pointer sm:pl-2"
                  @click="closeAvsModal"
                >
                  Edit Address
                </div>
              </div>
              <MailingAddress :address="addressProvided" class="mt-2 font-normal" />
              <div class="mt-4 text-xs text-red-700">Your address may be undeliverable</div>

              <ThemedButton class="mt-4" theme="white" @click="useEnteredAddress">
                Use Address as Entered
              </ThemedButton>
            </div>
          </div>
        </div>
        <div v-else-if="avsStatus.status === 'requireUnitNumber'">
          <div class="text-sm leading-5 md:text-base">
            Sorry, we think your apartment/suite/unit is missing or wrong. To proceed, please enter
            your apartment/suite/unit or use your address as entered.
          </div>
          <div class="block gap-20 mt-12 sm:flex">
            <div>
              <div class="font-bold">Confirm your Apartment/Suite/Unit:</div>
              <Field
                v-model="unitInfo"
                name="unitInfo"
                :rules="requireUnitInfo"
                v-slot="{ errors, field, meta }"
              >
                <div class="flex flex-col mt-5 sm:flex-row sm:items-center sm:block">
                  <div class="relative w-full">
                    <Input
                      v-bind="field"
                      class="placeholder-gray-500/50"
                      data-test="apartment-suite-unit-number"
                      type="text"
                      placeholder="APT 123"
                      aria-label="Apartment/Suite/Unit number"
                      :invalid="validatorFailed(meta)"
                    />
                    <FieldErrorIcon :showErrorIcon="validatorFailed(meta)" />
                    <InlineError
                      data-test="apartment-suite-unit-validation-error"
                      :errors="errors"
                      fieldName="Apartment/Suite/Unit Number"
                      :invalid="validatorFailed(meta)"
                    />
                  </div>
                  <div v-if="postalCodeChange" class="mt-5 text-sm italic leading-5 md:text-base">
                    We’ve also adjusted your postal code to
                    {{ avsStatus.suggestions[0].postalCode }}
                  </div>
                  <ThemedButton
                    class="mt-5 sm:ml-0"
                    :disabled="validatorFailed(meta)"
                    theme="white"
                    @click="confirmUnitNumber"
                  >
                    Confirm
                  </ThemedButton>
                </div>
              </Field>
            </div>
            <div class="mt-10 sm:mt-0">
              <div class="flex">
                <div class="font-bold">You Entered</div>
                <div
                  class="self-center pl-4 text-xs underline cursor-pointer sm:pl-2"
                  @click="closeAvsModal"
                >
                  Edit Address
                </div>
              </div>
              <MailingAddress class="mt-2 font-normal" :address="addressProvided" />
              <div class="mt-4 text-xs text-red-700">Your address may be undeliverable</div>
              <ThemedButton class="mt-4" theme="gray" @click="useEnteredAddress">
                Use Address as Entered
              </ThemedButton>
            </div>
          </div>
        </div>
        <div v-else-if="avsStatus.status === 'requireBuildingNumber'">
          <div class="text-sm leading-5 md:text-base">
            Sorry, we think your building or house number is missing or wrong. To proceed, please
            provide one or use your address as entered.
          </div>
          <div class="block gap-20 mt-12 sm:flex">
            <div>
              <div class="font-bold">Confirm your Building/House Number:</div>
              <Field
                v-model="houseInfo"
                name="buildingInfo"
                :rules="requireBuildingInfo"
                v-slot="{ errors, field, meta }"
              >
                <div class="flex items-center mt-5 sm:block">
                  <div class="relative w-full">
                    <Input
                      v-bind="field"
                      data-test="building-house-number"
                      type="text"
                      placeholder="Building/House Number"
                      aria-label="Building/House Number"
                      :invalid="validatorFailed(meta)"
                    />
                    <FieldErrorIcon :showErrorIcon="validatorFailed(meta)" />
                    <InlineError
                      :errors="errors"
                      fieldName="Building/House Number"
                      :invalid="validatorFailed(meta)"
                      data-test="building-house-validation-error"
                    />
                  </div>
                  <ThemedButton
                    class="ml-2 sm:mt-5 sm:ml-0"
                    :disabled="validatorFailed(meta)"
                    theme="white"
                    @click="confirmHouseNumber"
                  >
                    Confirm
                  </ThemedButton>
                </div>
              </Field>
            </div>
            <div class="mt-10 sm:mt-0">
              <div class="flex">
                <div class="font-bold">You Entered</div>
                <div
                  class="self-center pl-4 text-xs underline cursor-pointer sm:pl-2"
                  @click="closeAvsModal"
                >
                  Edit Address
                </div>
              </div>
              <MailingAddress :address="addressProvided" class="mt-2 font-normal" />
              <div class="mt-4 text-xs text-red-700">Your address may be undeliverable</div>
              <ThemedButton class="mt-4" theme="gray" @click="useEnteredAddress">
                Use Address as Entered
              </ThemedButton>
            </div>
          </div>
        </div>
        <div v-else-if="avsStatus.status === 'noMatches'">
          <div class="text-sm leading-5 md:text-base">
            Sorry, we could not find a match for your address. To proceed, please choose one of the
            options below.
          </div>
          <div class="mt-5">
            <div class="flex">
              <div class="font-bold">You Entered</div>
              <div
                class="self-center pl-4 text-xs underline cursor-pointer sm:pl-2"
                @click="closeAvsModal"
              >
                Edit Address
              </div>
            </div>
            <MailingAddress :address="addressProvided" class="mt-2 font-normal" />
            <div class="mt-4 text-xs text-red-700">Your address may be undeliverable</div>
            <ThemedButton class="mt-4" theme="gray" @click="useEnteredAddress">
              Use Address as Entered
            </ThemedButton>
          </div>
        </div>
        <div v-else>
          <div class="text-sm leading-5 md:text-base">
            We cannot validate your address. To ensure accurate delivery please confirm the address
            you entered:
          </div>
          <div class="mt-4 md:mt-10">
            <MailingAddress :address="addressProvided" class="font-normal" />
            <div class="mt-4 text-xs text-red-700">Your address may be undeliverable</div>
            <div class="flex gap-2 mt-2">
              <ThemedButton theme="gray" @click="useEnteredAddress">
                Use Address as Entered
              </ThemedButton>
              <ThemedButton theme="white" @click="closeAvsModal"> Edit Address </ThemedButton>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>
